import {
  type FC,
  type ReactNode,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Heading,
  Icon,
  Text,
  toast,
} from '@eluve/components';
import { useMedplum } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import type { MiniAppPanelProps } from '../../types';

import {
  CreateClinicalTaskSheet,
  EditClinicalTaskSheet,
} from './EditClinicalTaskSheet';
import { TaskExtractionInstructionsDialog } from './TaskExtractionInstructionsDialog';
import {
  CLINICAL_TASK_PRIORITY_CLASSES,
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUS_LABELS,
  asClinicalTaskStatus,
  asPriority,
  categoryIcon,
  categoryLabel,
} from './constants';
import {
  getTasksPanelSnapshot,
  subscribeTasksPanel,
} from './tasks-panel-store';
import {
  type AcceptedClinicalTask,
  type PendingClinicalTask,
  useClinicalTasksPanelState,
} from './useClinicalTasksPanelState';
import { usePatientSummary } from './usePatientSummary';

/** One row of the unified list — a task either awaiting review or accepted.
 *  The two kinds render through the same card; only the status badge and the
 *  action row differ. */
type PanelTask =
  | { readonly kind: 'pending'; readonly task: PendingClinicalTask }
  | { readonly kind: 'accepted'; readonly task: AcceptedClinicalTask };

/** One "label: value" line on the card; renders nothing without a value. */
const DetailRow: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="flex items-baseline gap-3">
      <Text size="sm" color="tertiary" className="w-24 shrink-0">
        {label}
      </Text>
      <Text size="sm" weight="medium">
        {value}
      </Text>
    </div>
  ) : null;

/** Prototype-styled priority chip (P1 · Highest … P4 · Low), or nothing. */
const PriorityChip: FC<{ priority: string | null }> = ({ priority }) => {
  const known = asPriority(priority);
  if (!known) return null;
  return (
    <span
      className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-xs font-semibold ${CLINICAL_TASK_PRIORITY_CLASSES[known]}`}
      data-testid="clinical-task-priority"
    >
      {CLINICAL_TASK_PRIORITY_LABELS[known]}
    </span>
  );
};

/**
 * The Clinical Tasks inline panel (ENG-5223): AI-suggested follow-up actions
 * awaiting review, then the accepted set tracked Todo → Done. Renders nothing
 * unless the tenant CLINICAL_TASKS flag AND the same-named PostHog user flag
 * are both on (extraction keeps running server-side regardless), or when
 * there is nothing at all to show.
 */
export const ClinicalTasksPanel: FC<MiniAppPanelProps> = ({ context }) => {
  const tenantFlagOn = useIsFeatureFlagEnabled('CLINICAL_TASKS');
  const userFlagOn = useIsPostHogFeatureFlagEnabled('CLINICAL_TASKS');

  if (!tenantFlagOn || !userFlagOn) return null;
  return <ClinicalTasksPanelContent context={context} />;
};

const ClinicalTasksPanelContent: FC<MiniAppPanelProps> = ({ context }) => {
  const { tenantId, appointmentId } = context;
  const state = useClinicalTasksPanelState({ tenantId, appointmentId });
  const [editing, setEditing] = useState<PanelTask | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  // Create-sheet context: the session's patient (read-only row) and the
  // current clinician (default assignee).
  const patient = usePatientSummary(context.patientId);
  const { practitionerId } = useMedplum();
  // The Tasks action button toggles `collapsed`; the hook above keeps polling
  // while collapsed so the button's count stays live. `extraction` gates the
  // empty panel while a run is still producing tasks.
  const { collapsed, extraction } = useSyncExternalStore(
    subscribeTasksPanel,
    getTasksPanelSnapshot,
  );

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    const ok = await state.decide(id, decision);
    if (ok) {
      toast.success(
        decision === 'approve' ? 'Task approved.' : 'Task removed.',
      );
    } else {
      toast.error('Could not submit the decision.');
    }
  };

  const removeAccepted = async (task: (typeof state.accepted)[number]) => {
    const ok = await state.removeAccepted(task);
    if (ok) {
      toast.success('Task removed.');
    } else {
      toast.error('Could not remove the task.');
    }
  };

  const regenerate = async () => {
    setConfirmingRegenerate(false);
    const ok = await state.regenerate();
    if (ok) {
      toast.success('Regenerating tasks…');
    } else {
      toast.error('Could not regenerate the tasks.');
    }
  };

  // ONE list, position-stable: accepting flips a card's status in place
  // instead of moving it to a separate Accepted box. The task's id survives
  // the pending→accepted transition, so an order ref pins each card to the
  // slot it first appeared in across refreshes; ids that vanish (reject,
  // supersede) drop out, new ones append.
  const orderRef = useRef<string[]>([]);
  const tasks = useMemo<PanelTask[]>(() => {
    const byId = new Map<string, PanelTask>();
    for (const t of state.pending) byId.set(t.id, { kind: 'pending', task: t });
    for (const t of state.accepted) {
      byId.set(t.id, { kind: 'accepted', task: t });
    }
    const order = orderRef.current.filter((id) => byId.has(id));
    for (const id of byId.keys()) {
      if (!order.includes(id)) order.push(id);
    }
    orderRef.current = order;
    return order.flatMap((id) => byId.get(id) ?? []);
  }, [state.pending, state.accepted]);

  // Loading or collapsed → section contributes no DOM. Zero-task sessions
  // start collapsed (store default), so an open rail is deliberate — always
  // render content (header, empty state, Add task), never a blank shell.
  // Sheets render OUTSIDE this gate: portal overlays; unmounting an open
  // editor mid-edit discards un-debounced typing.
  const hasTasks = tasks.length > 0;
  const showSection = !state.loading && !collapsed;
  // An extraction is running (or was just requested — the marker lags the
  // request). The Regenerate button doubles as the progress indicator: when
  // accepted tasks keep the list populated, the empty-slot "Extracting
  // tasks…" message never shows, so the button is the only signal.
  const regenerateActive = state.regenerating || extraction === 'generating';

  return (
    <>
      {editing?.kind === 'pending' ? (
        <EditClinicalTaskSheet
          task={editing.task}
          practitioners={state.practitioners}
          onSave={async (edit) => {
            const ok = await state.applyEdit(editing.task.id, edit);
            // Picking a decided status attests the draft — it leaves the
            // pending pile, and the route can no longer edit it. Close so any
            // further edit reopens from the (now accepted) card's browser-
            // direct path. Staying Draft keeps the sheet open for more edits.
            const chosen = asClinicalTaskStatus(edit.status);
            if (ok && chosen && chosen !== 'draft') setEditing(null);
            return ok;
          }}
          onAccept={() => state.decide(editing.task.id, 'approve')}
          onDelete={() => state.decide(editing.task.id, 'reject')}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {editing?.kind === 'accepted' ? (
        <EditClinicalTaskSheet
          task={editing.task}
          practitioners={state.practitioners}
          onSave={(edit) => state.editAccepted(editing.task, edit)}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {creating ? (
        <CreateClinicalTaskSheet
          sourceLabel="From encounter"
          sessionTitle={context.sessionTitle ?? null}
          patient={patient}
          practitioners={state.practitioners}
          defaultOwnerReference={
            practitionerId ? `Practitioner/${practitionerId}` : null
          }
          onCreate={(input) => state.createTask(input)}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {/* Portal overlays like the sheets: an open dialog must survive the
          section hiding itself (regenerate can empty the pending list, which
          collapses the section while the run is in flight). */}
      {instructionsOpen ? (
        <TaskExtractionInstructionsDialog
          tenantId={tenantId}
          onClose={() => setInstructionsOpen(false)}
        />
      ) : null}
      <AlertDialog
        open={confirmingRegenerate}
        onOpenChange={setConfirmingRegenerate}
      >
        <AlertDialogContent data-testid="clinical-tasks-regenerate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate tasks?</AlertDialogTitle>
            <AlertDialogDescription>
              Regenerating tasks for this encounter will change the currently
              suggested tasks to cancelled status, and tasks you removed earlier
              may be suggested again. Tasks you have already approved are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={regenerate}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {showSection ? (
        <section aria-label="Clinical Tasks" data-testid="clinical-tasks-panel">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Icon name="List" size="lg" />
              <Heading size="xl" weight="semibold">
                Tasks
              </Heading>
              <Badge
                variant="secondary"
                className="bg-bg-indigo-subtle-primary text-text-indigo-subtle"
                data-testid="clinical-tasks-count"
              >
                {state.pending.length + state.accepted.length}
              </Badge>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={state.mutating || regenerateActive}
                  onClick={() => setConfirmingRegenerate(true)}
                  data-testid="clinical-tasks-regenerate"
                >
                  <Icon
                    name="RefreshCw"
                    size="sm"
                    className={regenerateActive ? 'animate-spin' : undefined}
                  />
                  {regenerateActive ? 'Regenerating…' : 'Regenerate'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Task extraction instructions"
                  disabled={state.mutating}
                  onClick={() => setInstructionsOpen(true)}
                  data-testid="clinical-tasks-instructions"
                >
                  <Icon name="Settings" size="sm" />
                </Button>
              </div>
            </div>
            {/* Extraction in flight (regenerate or `generating` marker):
                the spinning button is the indicator — no premature
                "No tasks yet.". */}
            {!hasTasks && !regenerateActive ? (
              <Text size="sm" color="tertiary">
                No tasks yet.
              </Text>
            ) : null}
            <div
              className="flex w-full flex-col gap-3"
              data-testid="clinical-tasks-list"
            >
              {tasks.map((entry) => {
                const t = entry.task;
                const done = entry.kind === 'accepted' && entry.task.done;
                return (
                  <div
                    key={t.id}
                    className="flex flex-col gap-3 rounded-xl border border-border-primary bg-white p-4"
                    data-testid={
                      entry.kind === 'pending'
                        ? 'clinical-task-pending-row'
                        : 'clinical-task-accepted-row'
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Badge
                        variant="secondary"
                        className="gap-1 bg-bg-indigo-subtle-primary text-text-indigo-subtle"
                      >
                        <Icon name={categoryIcon(t.category)} size="xs" />
                        {categoryLabel(t.category)}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="font-medium">
                          {
                            CLINICAL_TASK_STATUS_LABELS[
                              asClinicalTaskStatus(t.status) ?? 'requested'
                            ]
                          }
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Edit"
                          disabled={state.mutating}
                          onClick={() => setEditing(entry)}
                        >
                          <Icon name="Pencil" size="sm" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Remove"
                          disabled={state.mutating}
                          onClick={() =>
                            entry.kind === 'pending'
                              ? decide(t.id, 'reject')
                              : removeAccepted(entry.task)
                          }
                        >
                          <Icon name="Trash2" size="sm" />
                        </Button>
                      </div>
                    </div>
                    <Text
                      size="md"
                      weight="semibold"
                      data-testid="clinical-task-description"
                      className={done ? 'line-through opacity-60' : ''}
                    >
                      {t.description}
                    </Text>
                    {t.evidence ? (
                      <Text
                        size="xs"
                        color="tertiary"
                        className="line-clamp-2 italic"
                      >
                        source: “{t.evidence}”
                      </Text>
                    ) : null}
                    <div className="border-t border-border-secondary" />
                    <PriorityChip priority={t.priority} />
                    <div className="flex flex-col gap-2">
                      <DetailRow label="For" value={t.forDisplay} />
                      <DetailRow
                        label="Assigned to"
                        value={state.practitionerName(t.ownerReference)}
                      />
                      <DetailRow label="Planned" value={t.dueDate} />
                    </div>
                    {/* Edit + Remove live in the header (pencil / trash) for
                        both kinds. Pending keeps one footer action — Approve;
                        accepted cards have no footer. */}
                    {entry.kind === 'pending' ? (
                      <div className="flex gap-1 pt-1">
                        <Button
                          size="sm"
                          variant="brand"
                          disabled={state.mutating}
                          onClick={() => decide(t.id, 'approve')}
                        >
                          Approve
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline"
              className="w-full"
              disabled={state.mutating}
              onClick={() => setCreating(true)}
              data-testid="clinical-tasks-add"
            >
              <Icon name="Plus" size="sm" />
              Add task
            </Button>
          </div>
        </section>
      ) : null}
    </>
  );
};
